import React, {useMemo, useEffect, useRef, useState} from 'react';
import * as THREE from 'three';
import {useAppSelector} from '../../app/useAppSelector';
import {useGroundSnap} from '../hooks/useGroundSnap';
import {useMarkerActions} from './useMarkerActions';
import {MarkerHoverLabel} from './MarkerHoverLabel';
import type {LocationData} from '@shared/types/rtlsTypes';

// Tooltip enter delay matches the standard map idiom (Google Maps ~200 ms).
const HOVER_DELAY_MS = 200;

// `onDeleteRequest` was the right-click delete path. Removed — the previous
// onContextMenu rendered no visible menu; deletes now go through the
// RTLSPanel's LocationsSection which already has a delete button. The prop
// is kept as optional + ignored for one release in case any caller still
// passes it; remove on the next pass.
interface LocationMarkersProps {
  onDeleteRequest?: unknown;
}

// Draw location type icon inside avatar circle
function drawLocationIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  type: string,
) {
  ctx.fillStyle = '#E6E6E6';
  ctx.strokeStyle = '#E6E6E6';
  ctx.lineWidth = radius * 0.08;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const s = radius * 0.55;

  switch (type) {
    case 'entry': {
      // Triangle (portal/entry)
      ctx.beginPath();
      ctx.moveTo(cx, cy - s * 0.7);
      ctx.lineTo(cx - s * 0.65, cy + s * 0.5);
      ctx.lineTo(cx + s * 0.65, cy + s * 0.5);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'station': {
      // Rounded square (station)
      const half = s * 0.5;
      ctx.beginPath();
      ctx.roundRect(cx - half, cy - half, half * 2, half * 2, 3);
      ctx.fill();
      break;
    }
    case 'safety': {
      // Cross (safety)
      const arm = s * 0.55, thick = s * 0.22;
      ctx.beginPath();
      ctx.roundRect(cx - arm, cy - thick, arm * 2, thick * 2, 2);
      ctx.fill();
      ctx.beginPath();
      ctx.roundRect(cx - thick, cy - arm, thick * 2, arm * 2, 2);
      ctx.fill();
      break;
    }
    case 'stope': {
      // Diamond
      ctx.beginPath();
      ctx.moveTo(cx, cy - s * 0.65);
      ctx.lineTo(cx + s * 0.5, cy);
      ctx.lineTo(cx, cy + s * 0.65);
      ctx.lineTo(cx - s * 0.5, cy);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'infrastructure': {
      // Gear
      ctx.font = `bold ${radius * 0.7}px -apple-system, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⚙', cx, cy);
      break;
    }
    case 'tunnel': {
      // Horizontal bar
      const w = s * 1.0, h = s * 0.3;
      ctx.beginPath();
      ctx.roundRect(cx - w / 2, cy - h / 2, w, h, 2);
      ctx.fill();
      break;
    }
    default: {
      // Pin/location marker
      ctx.beginPath();
      ctx.arc(cx, cy - s * 0.15, s * 0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.2, cy + s * 0.1);
      ctx.lineTo(cx, cy + s * 0.6);
      ctx.lineTo(cx + s * 0.2, cy + s * 0.1);
      ctx.closePath();
      ctx.fill();
      break;
    }
  }
}

// Texture cache for location avatars (keyed by type)
const locationTextureCache = new Map<string, THREE.Texture>();

function getCachedLocationTexture(type: string): THREE.Texture {
  if (locationTextureCache.has(type)) return locationTextureCache.get(type)!;

  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2, cy = size / 2;
  const radius = size * 0.42;
  const borderWidth = size * 0.035;

  ctx.clearRect(0, 0, size, size);

  // Shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 2;

  // Border ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = '#555555';
  ctx.fill();
  ctx.restore();

  // Inner circle
  ctx.beginPath();
  ctx.arc(cx, cy, radius - borderWidth, 0, Math.PI * 2);
  ctx.fillStyle = '#333333';
  ctx.fill();

  drawLocationIcon(ctx, cx, cy, radius, type);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  locationTextureCache.set(type, texture);
  return texture;
}

/** Dispose and clear the module-level location texture cache. */
export function clearLocationTextures(): void {
  locationTextureCache.forEach(tex => tex.dispose());
  locationTextureCache.clear();
}

const MARKER_SIZE = 14;
// Tiny lift so the sprite stays above the ground mesh in the depth buffer
// (sprites still depth-test) without visibly floating off the point.
const AVATAR_Z_OFFSET = 1;
const LABEL_Z_OFFSET = AVATAR_Z_OFFSET + MARKER_SIZE / 2 + 5;
const PROXIMITY_THRESHOLD = 20;
const SPREAD_SPACING = 12;

const LocationSprite: React.FC<{
  location: LocationData;
}> = React.memo(({location}) => {
  const {onMarkerDoubleClick} = useMarkerActions();
  const [hovered, setHovered] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const texture = useMemo(
    () => getCachedLocationTexture(location.type),
    [location.type],
  );

  const avatarMaterial = useMemo(() => {
    const mat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    });
    mat.toneMapped = false;
    return mat;
  }, [texture]);

  const {labelMaterial, spriteWidth} = useMemo(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const s = 4;
    const fontSize = 24 * s;
    const paddingH = 20 * s;
    const paddingV = 12 * s;

    ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
    const nameW = ctx.measureText(location.name).width;
    const totalW = paddingH + nameW + paddingH;
    const totalH = fontSize + paddingV * 2;
    const borderRadius = totalH / 2;

    canvas.width = totalW;
    canvas.height = totalH;

    // Shadow + background
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 8 * s;
    ctx.shadowOffsetY = 2 * s;
    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.fillStyle = 'rgba(30, 36, 51, 0.85)';
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.fillStyle = 'rgba(30, 36, 51, 0.85)';
    ctx.fill();

    // Border
    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.strokeStyle = 'rgba(75, 85, 99, 0.5)';
    ctx.lineWidth = 1.5 * s;
    ctx.stroke();

    // Name text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(location.name, totalW / 2, totalH / 2);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;

    const aspect = totalW / totalH;
    const spriteH = 5;
    const mat = new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
    });
    mat.toneMapped = false;
    return {labelMaterial: mat, spriteWidth: spriteH * aspect};
  }, [location.name]);

  const spriteHeight = 5;

  // Hover (desktop only — touch never fires onPointerEnter without a
  // preceding mousemove). 200 ms delay matches the standard map idiom so
  // a casual mouseover doesn't flash tooltips everywhere.
  const handlePointerEnter = () => {
    document.body.style.cursor = 'pointer';
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHovered(true), HOVER_DELAY_MS);
  };
  const handlePointerLeave = () => {
    document.body.style.cursor = 'default';
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHovered(false);
  };
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    document.body.style.cursor = 'default';
  }, []);

  const handleDoubleClick = (e: any) => {
    e.stopPropagation();
    onMarkerDoubleClick(location.position);
  };

  return (
    <group position={[location.position.x, location.position.y, location.position.z]}>
      <sprite
        material={avatarMaterial}
        scale={[MARKER_SIZE, MARKER_SIZE, 1]}
        position={[0, 0, AVATAR_Z_OFFSET]}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onDoubleClick={handleDoubleClick}
      />
      <sprite
        material={labelMaterial}
        position={[0, 0, LABEL_Z_OFFSET]}
        scale={[spriteWidth, spriteHeight, 1]}
      />
      <MarkerHoverLabel
        visible={hovered}
        position={[0, 0, AVATAR_Z_OFFSET + MARKER_SIZE]}
        primary={location.name}
        secondary={`Location · ${location.type}`}
      />
    </group>
  );
});

LocationSprite.displayName = 'LocationSprite';

export const LocationMarkers: React.FC<LocationMarkersProps> = (_props) => {
  // Redux selectors replace eventBus subscriptions
  const locationsRecord = useAppSelector(s => s.rtls.locations);
  const assetsRecord = useAppSelector(s => s.rtls.assets);
  const panelFilter = useAppSelector(s => s.rtls.panelFilter);

  const locations = useMemo(() => Object.values(locationsRecord), [locationsRecord]);

  // Derive RTLS positions from Redux (replaces rtlsRef updated via UPDATE_RTLS_POSITIONS event)
  const rtlsPositions = useMemo(
    () => Object.values(assetsRecord).map(a => ({x: a.position.x, y: a.position.y})),
    [assetsRecord],
  );

  const {snapToGround} = useGroundSnap(8);

  // Cleanup texture cache on unmount
  useEffect(() => {
    return () => {
      clearLocationTextures();
    };
  }, []);

  const snappedLocations = useMemo(() => {
    return locations.map(loc => {
      const snapped = snapToGround(loc.position.x, loc.position.y, loc.position.z);

      // Count nearby RTLS markers
      let nearbyCount = 0;
      for (const rp of rtlsPositions) {
        const dx = snapped.x - rp.x;
        const dy = snapped.y - rp.y;
        if (Math.sqrt(dx * dx + dy * dy) < PROXIMITY_THRESHOLD) {
          nearbyCount++;
        }
      }

      // Offset to the left of the RTLS cluster so they sit side by side
      if (nearbyCount > 0) {
        const offsetX = -((nearbyCount - 1) / 2 + 1) * SPREAD_SPACING;
        return {...loc, position: {...snapped, x: snapped.x + offsetX}};
      }
      return {...loc, position: snapped};
    });
  }, [locations, snapToGround, rtlsPositions]);

  // Hide entirely unless the panel is set to All or Locations.
  if (panelFilter !== 'all' && panelFilter !== 'location') return null;

  return (
    <group>
      {snappedLocations.map(loc => (
        <LocationSprite key={loc.id} location={loc} />
      ))}
    </group>
  );
};

LocationMarkers.displayName = 'LocationMarkers';
